import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FiveColumnTable from "@/charts/FiveColumnTable";
import useLoadData from "@/hooks/useLoadData";
import CustomDropdown from "@/components/ui/CustomDropdown";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { showApiError, showSuccess } from "@/utils";

// Brands auto-created by query research, or curated brands it suggests
// values for, wait for a super admin's review
const hasPendingValues = (brand) =>
  Boolean(brand.pending_variations?.length || brand.pending_domains?.length);

const needsReview = (brand) =>
  Boolean(brand.auto_created) || hasPendingValues(brand);

const splitList = (value) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const PendingValues = ({
  title,
  values,
  field,
  formValue,
  addPendingValue,
  disabled,
}) => {
  const { t } = useTranslation();
  if (!values?.length) return null;
  const added = new Set(splitList(formValue).map((v) => v.toLowerCase()));

  return (
    <div className="mt-2">
      <div className="fw-semibold">{title}</div>
      <div className="d-flex flex-wrap gap-1 mt-1">
        {values.map((value) => {
          const isAdded = added.has(value.toLowerCase());
          return (
            <button
              key={value}
              type="button"
              className={`btn btn-sm ${isAdded ? "btn-success" : "btn-outline-secondary"}`}
              style={{ fontSize: "0.75rem", padding: "0.1rem 0.5rem" }}
              onClick={() => addPendingValue(field, value)}
              disabled={disabled || isAdded}
              title={
                isAdded
                  ? t("superAdmin.brands.pendingValueAdded", "Added to brand")
                  : t("superAdmin.brands.addPendingValue", "Add to brand")
              }
            >
              <i className={`bi ${isAdded ? "bi-check" : "bi-plus"}`}></i>{" "}
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const BrandModal = ({
  isEdit = false,
  brandFormData,
  handleBrandInputChange,
  addPendingValue,
  isBrandSubmitting,
  setShowAddBrandModal,
  setShowEditBrandModal,
  resetBrandForm,
  handleBrandSubmit,
  handleBrandEditSubmit,
  allClients,
  allClientsLoaded,
}) => {
  const { t } = useTranslation();
  const modalTitle = isEdit
    ? t("superAdmin.brands.editBrand", "Edit Brand")
    : t("superAdmin.brands.addNewBrand", "Add New Brand");
  const submitHandler = isEdit ? handleBrandEditSubmit : handleBrandSubmit;
  const submitText = isEdit
    ? t("superAdmin.brands.updateBrand", "Update Brand")
    : t("superAdmin.brands.createBrand", "Create Brand");
  const loadingText = isEdit
    ? t("common.editing", "Updating...")
    : t("common.adding", "Creating...");

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{modalTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                if (isEdit) {
                  setShowEditBrandModal(false);
                } else {
                  setShowAddBrandModal(false);
                }
                resetBrandForm();
              }}
              disabled={isBrandSubmitting}
            ></button>
          </div>
          <form onSubmit={submitHandler}>
            <div className="modal-body">
              {isEdit && needsReview(brandFormData) && (
                <div className="alert alert-warning py-2 mb-3">
                  {brandFormData.auto_created && (
                    <div className="mb-2" style={{ fontSize: "0.85rem" }}>
                      <span className="badge bg-warning text-dark me-2">
                        {t("superAdmin.brands.autoCreated", "Auto-created")}
                      </span>
                      {brandFormData.approve_review
                        ? t(
                            "superAdmin.brands.autoCreatedWillBeApproved",
                            "The auto-created tag will be removed when you save.",
                          )
                        : t(
                            "superAdmin.brands.autoCreatedPendingApproval",
                            "This brand was created automatically and is pending approval.",
                          )}
                      <div className="mt-1">
                        {t(
                          "superAdmin.brands.rejectionNote",
                          "Auto-created names and domains you remove from this brand won't be auto-created again.",
                        )}
                      </div>
                    </div>
                  )}
                  {hasPendingValues(brandFormData) && (
                    <div className="mb-2" style={{ fontSize: "0.85rem" }}>
                      <span className="badge bg-info text-dark me-2">
                        {t("superAdmin.brands.pendingReview", "Pending review")}
                      </span>
                      {t(
                        "superAdmin.brands.pendingReviewMessage",
                        "Query research suggests adding these values to this brand. They don't count towards the brand until you add them.",
                      )}
                      <PendingValues
                        title={t(
                          "superAdmin.brands.pendingVariations",
                          "Suggested name variations",
                        )}
                        values={brandFormData.pending_variations}
                        field="brand_name_variations"
                        formValue={brandFormData.brand_name_variations}
                        addPendingValue={addPendingValue}
                        disabled={isBrandSubmitting}
                      />
                      <PendingValues
                        title={t(
                          "superAdmin.brands.pendingDomains",
                          "Suggested domains",
                        )}
                        values={brandFormData.pending_domains}
                        field="domains"
                        formValue={brandFormData.domains}
                        addPendingValue={addPendingValue}
                        disabled={isBrandSubmitting}
                      />
                    </div>
                  )}
                  <div className="form-check mb-0">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="approve-review"
                      checked={brandFormData.approve_review}
                      onChange={(e) =>
                        handleBrandInputChange({
                          target: {
                            name: "approve_review",
                            value: e.target.checked,
                          },
                        })
                      }
                      disabled={isBrandSubmitting}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="approve-review"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {brandFormData.auto_created
                        ? t(
                            "superAdmin.brands.autoCreatedRemoveTag",
                            "Remove auto-created tag (approve brand)",
                          )
                        : t(
                            "superAdmin.brands.approveReview",
                            "Mark as reviewed",
                          )}
                    </label>
                    {hasPendingValues(brandFormData) && (
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {t(
                          "superAdmin.brands.approveRejectsPending",
                          "Suggested values you haven't added will be rejected and won't be suggested again.",
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Client ID Dropdown */}
              <div className="mb-3">
                <label htmlFor="client-id" className="form-label">
                  {t("superAdmin.brands.clientId", "Client ID")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <CustomDropdown
                  triggerId="client-id"
                  options={[
                    {
                      value: "",
                      label: t(
                        "superAdmin.brands.selectClient",
                        "Select a client",
                      ),
                    },
                    ...(allClientsLoaded && allClients
                      ? allClients.map((client) => ({
                          value: client.client_id,
                          label: client.client_id,
                        }))
                      : []),
                  ]}
                  value={brandFormData.client_id}
                  onChange={(val) =>
                    handleBrandInputChange({
                      target: { name: "client_id", value: val },
                    })
                  }
                  disabled={isBrandSubmitting || isEdit}
                />
                {isEdit && (
                  <small className="text-muted">
                    {t(
                      "superAdmin.brands.clientIdCannotChange",
                      "Client ID cannot be changed during edit",
                    )}
                  </small>
                )}
              </div>

              {/* Brand Type Selection */}
              <div className="mb-3">
                <label className="form-label">
                  {t("superAdmin.brands.brandType", "Brand Type")}{" "}
                  {!isEdit && <span className="text-danger">*</span>}
                </label>
                <div className="btn-group w-100" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="brand_type"
                    id="brand-type-own"
                    value="own"
                    checked={brandFormData.brand_type === "own"}
                    onChange={handleBrandInputChange}
                    disabled={isBrandSubmitting}
                    required={!isEdit}
                  />
                  <label
                    className="btn btn-outline-primary"
                    htmlFor="brand-type-own"
                    style={{
                      borderColor: "var(--brand-color-dark)",
                      color:
                        brandFormData.brand_type === "own"
                          ? "#fff"
                          : "var(--brand-color-dark)",
                      backgroundColor:
                        brandFormData.brand_type === "own"
                          ? "var(--brand-color-dark)"
                          : "transparent",
                    }}
                  >
                    {t("superAdmin.brands.ownBrand", "Own Brand")}
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="brand_type"
                    id="brand-type-competitor"
                    value="competitor"
                    checked={brandFormData.brand_type === "competitor"}
                    onChange={handleBrandInputChange}
                    disabled={isBrandSubmitting}
                    required={!isEdit}
                  />
                  <label
                    className="btn btn-outline-primary"
                    htmlFor="brand-type-competitor"
                    style={{
                      borderColor: "var(--brand-color-dark)",
                      color:
                        brandFormData.brand_type === "competitor"
                          ? "#fff"
                          : "var(--brand-color-dark)",
                      backgroundColor:
                        brandFormData.brand_type === "competitor"
                          ? "var(--brand-color-dark)"
                          : "transparent",
                    }}
                  >
                    {t("superAdmin.brands.competitorBrand", "Competitor Brand")}
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="brand-name" className="form-label">
                  {t("superAdmin.brands.brandName", "Brand Name")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="brand-name"
                  name="name"
                  value={brandFormData.name}
                  onChange={handleBrandInputChange}
                  required
                  disabled={isBrandSubmitting}
                  placeholder={t(
                    "superAdmin.brands.enterBrandName",
                    "Enter brand name",
                  )}
                  style={{ lineHeight: "1.5" }}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="brand-domains" className="form-label">
                  {t("superAdmin.brands.domains", "Domains")}
                </label>
                <textarea
                  className="form-control"
                  id="brand-domains"
                  name="domains"
                  value={brandFormData.domains}
                  onChange={handleBrandInputChange}
                  disabled={isBrandSubmitting}
                  placeholder="example.com, another-domain.com"
                  style={{
                    lineHeight: "1.6",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                  rows="3"
                />
                <small className="text-muted">
                  {t(
                    "superAdmin.brands.domainsHint",
                    "Enter domains separated by commas",
                  )}
                </small>
              </div>
              <div className="mb-3">
                <label htmlFor="brand-name-variations" className="form-label">
                  {t(
                    "superAdmin.brands.brandNameVariationsDesc",
                    "Brand name variations - These keywords are treated as synonyms or variations of the brand name and will be used to highlight brand mentions.",
                  )}
                </label>
                <textarea
                  className="form-control"
                  id="brand-name-variations"
                  name="brand_name_variations"
                  value={brandFormData.brand_name_variations}
                  onChange={handleBrandInputChange}
                  disabled={isBrandSubmitting}
                  placeholder="keyword1, keyword2, keyword3"
                  style={{
                    lineHeight: "1.6",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                  rows="3"
                />
                <small className="text-muted">
                  {t(
                    "superAdmin.brands.brandNameVariationsHint",
                    "Enter brand name variations separated by commas",
                  )}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (isEdit) {
                    setShowEditBrandModal(false);
                  } else {
                    setShowAddBrandModal(false);
                  }
                  resetBrandForm();
                }}
                disabled={isBrandSubmitting}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  isBrandSubmitting ||
                  !brandFormData.name.trim() ||
                  !brandFormData.client_id ||
                  (!isEdit && !brandFormData.brand_type)
                }
                style={{
                  backgroundColor: "var(--btn-solid-bg)",
                  color: "var(--btn-solid-text)",
                }}
              >
                {isBrandSubmitting ? loadingText : submitText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

function SuperAdminBrand() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [allClients, allClientsLoaded] = useLoadData(
    `${BACKEND_URL}/api/v1/client/all`,
  );

  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [showEditBrandModal, setShowEditBrandModal] = useState(false);
  const [isBrandSubmitting, setIsBrandSubmitting] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isBrandDeleting, setIsBrandDeleting] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandFormData, setBrandFormData] = useState({
    client_id: "",
    brand_type: "",
    name: "",
    domains: "",
    brand_name_variations: "",
    auto_created: false,
    pending_variations: [],
    pending_domains: [],
    approve_review: false,
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshAllBrands = () => setRefreshTrigger((prev) => prev + 1);
  const [allBrands, allBrandsLoaded, allBrandErrors] = useLoadData(
    `${BACKEND_URL}/api/v1/brand/all?refresh=${refreshTrigger}`,
  );

  const handleBrandInputChange = (e) => {
    const { name, value } = e.target;
    setBrandFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Append a suggested value to the brand's comma-separated domains or
  // variations, which accepts it when the brand is saved
  const addPendingValue = (field, value) => {
    setBrandFormData((prev) => {
      const values = splitList(prev[field]);
      if (values.some((v) => v.toLowerCase() === value.toLowerCase())) {
        return prev;
      }
      return { ...prev, [field]: [...values, value].join(", ") };
    });
  };

  const resetBrandForm = () => {
    setBrandFormData({
      client_id: "",
      brand_type: "",
      name: "",
      domains: "",
      brand_name_variations: "",
      auto_created: false,
      pending_variations: [],
      pending_domains: [],
      approve_review: false,
    });
    setEditingBrand(null);
  };

  const handleBrandSubmit = async (e) => {
    e.preventDefault();
    setIsBrandSubmitting(true);

    try {
      const brandData = {
        client_id: brandFormData.client_id,
        brand_type: brandFormData.brand_type,
        name: brandFormData.name.trim(),
        domains: brandFormData.domains
          ? brandFormData.domains
              .split(",")
              .map((domain) => domain.trim())
              .filter(Boolean)
          : [],
        brand_name_variations: brandFormData.brand_name_variations
          ? brandFormData.brand_name_variations
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : null,
      };

      const response = await fetch(`${BACKEND_URL}/api/v1/brand`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(brandData),
      });

      const result = await response.json();

      if (response.ok) {
        setShowAddBrandModal(false);
        resetBrandForm();
        refreshAllBrands();
        showSuccess(
          result.message ||
            t(
              "superAdmin.brands.brandCreatedSuccess",
              "Brand created successfully!",
            ),
        );
      } else {
        showApiError(
          result.detail ||
            t(
              "superAdmin.brands.brandCreateError",
              "Failed to create brand",
            ),
        );
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      showApiError(
        t(
          "superAdmin.brands.brandCreateError",
          "An error occurred while creating the brand",
        ),
      );
    } finally {
      setIsBrandSubmitting(false);
    }
  };

  const handleBrandEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBrand) return;

    setIsBrandSubmitting(true);

    try {
      const brandData = {
        client_id: brandFormData.client_id,
        brand_type: brandFormData.brand_type || null,
        name: brandFormData.name.trim(),
        domains: brandFormData.domains
          ? brandFormData.domains
              .split(",")
              .map((domain) => domain.trim())
              .filter(Boolean)
          : [],
        brand_name_variations: brandFormData.brand_name_variations
          ? brandFormData.brand_name_variations
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : null,
        // Approves the brand: removes the auto-created tag and rejects the
        // pending values that weren't added to the brand
        ...(brandFormData.approve_review && { auto_created: false }),
      };

      const response = await fetch(
        `${BACKEND_URL}/api/v1/brand/${encodeURIComponent(editingBrand.name)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(brandData),
        },
      );

      const result = await response.json();

      if (response.ok) {
        setShowEditBrandModal(false);
        resetBrandForm();
        refreshAllBrands();
        showSuccess(
          result.message ||
            t(
              "superAdmin.brands.brandUpdatedSuccess",
              "Brand updated successfully!",
            ),
        );
      } else {
        showApiError(
          result.detail ||
            t(
              "superAdmin.brands.brandUpdateError",
              "Failed to update brand",
            ),
        );
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      showApiError(
        t(
          "superAdmin.brands.brandUpdateError",
          "An error occurred while updating the brand",
        ),
      );
    } finally {
      setIsBrandSubmitting(false);
    }
  };

  const handleBrandEdit = (brand) => {
    setEditingBrand(brand);

    // Determine brand_type by checking client's own_brand and competitor_brands
    let brandType = "";
    const client = allClients?.find((c) => c.client_id === brand.client_id);
    if (client) {
      if (client.own_brand === brand.name) {
        brandType = "own";
      } else if (client.competitor_brands?.includes(brand.name)) {
        brandType = "competitor";
      }
    }

    setBrandFormData({
      client_id: brand.client_id || "",
      brand_type: brandType,
      name: brand.name || "",
      domains: brand.domains ? brand.domains.join(", ") : "",
      brand_name_variations: brand.brand_name_variations
        ? brand.brand_name_variations.join(", ")
        : "",
      auto_created: Boolean(brand.auto_created),
      pending_variations: brand.pending_variations || [],
      pending_domains: brand.pending_domains || [],
      approve_review: false,
    });
    setShowEditBrandModal(true);
  };

  const handleBrandDelete = (brand) => {
    setBrandToDelete(brand);
  };

  const handleConfirmDeleteBrand = async () => {
    if (!brandToDelete) return;
    setIsBrandDeleting(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/brand/${encodeURIComponent(brandToDelete.name)}?client_id=${encodeURIComponent(brandToDelete.client_id)}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );

      const result = await response.json().catch(() => null);

      if (response.ok) {
        refreshAllBrands();
        showSuccess(
          result?.message ||
            t(
              "superAdmin.brands.brandDeletedSuccess",
              "Brand deleted successfully!",
            ),
        );
        setBrandToDelete(null);
      } else {
        showApiError(
          result?.detail ||
            t(
              "superAdmin.brands.brandDeleteError",
              "Failed to delete brand",
            ),
        );
      }
    } catch (error) {
      console.error("Error deleting brand:", error);
      showApiError(
        t(
          "superAdmin.brands.brandDeleteError",
          "An error occurred while deleting the brand",
        ),
      );
    } finally {
      setIsBrandDeleting(false);
    }
  };

  // Filter brands based on search term and add action buttons
  const filteredBrands = useMemo(() => {
    let brands = allBrands;

    if (brandSearchTerm.trim() && allBrands) {
      brands = allBrands.filter((brand) => {
        const searchLower = brandSearchTerm.toLowerCase();
        const brandName = brand.name ? brand.name.toLowerCase() : "";
        const clientId = brand.client_id ? brand.client_id.toLowerCase() : "";

        if (brandName.includes(searchLower)) return true;
        if (clientId.includes(searchLower)) return true;

        if (brand.domains && Array.isArray(brand.domains)) {
          const domainMatch = brand.domains.some((domain) =>
            domain.toLowerCase().includes(searchLower),
          );
          if (domainMatch) return true;
        }

        if (
          brand.brand_name_variations &&
          Array.isArray(brand.brand_name_variations)
        ) {
          const keywordMatch = brand.brand_name_variations.some((keyword) =>
            keyword.toLowerCase().includes(searchLower),
          );
          if (keywordMatch) return true;
        }

        return false;
      });
    }

    // Add actions to each brand (5th column)
    return brands?.map((brand) => ({
      ...brand,
      // Always JSX: FiveColumnTable renders plain strings as HTML, and
      // auto-created names come from query research, not an admin
      name_display: (
        <span>
          {brand.name}
          {brand.auto_created && (
            <span
              className="badge bg-warning text-dark ms-1"
              title={t(
                "superAdmin.brands.autoCreatedTooltip",
                "Created automatically from query research. Edit the brand and remove this tag to approve it, or delete it if it was created wrongly; its values won't be auto-created again.",
              )}
            >
              {t("superAdmin.brands.autoCreated", "Auto-created")}
            </span>
          )}
          {hasPendingValues(brand) && (
            <span
              className="badge bg-info text-dark ms-1"
              title={t(
                "superAdmin.brands.pendingReviewTooltip",
                "Query research suggests adding: {{values}}. Edit the brand to review them.",
                {
                  values: [
                    ...(brand.pending_variations || []),
                    ...(brand.pending_domains || []),
                  ].join(", "),
                },
              )}
            >
              {t("superAdmin.brands.pendingReviewCount", "{{count}} pending", {
                count:
                  (brand.pending_variations?.length || 0) +
                  (brand.pending_domains?.length || 0),
              })}
            </span>
          )}
        </span>
      ),
      actions: (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary border-0 p-1"
            onClick={() => handleBrandEdit(brand)}
            title={t("common.edit", "Edit")}
            style={{ minWidth: "28px", height: "28px" }}
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger border-0 p-1"
            onClick={() => handleBrandDelete(brand)}
            title={t("common.delete", "Delete")}
            style={{ minWidth: "28px", height: "28px" }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    }));
    // eslint-disable-next-line
  }, [allBrands, brandSearchTerm, allClients, t]);

  return (
    <div>
      <h4 className="h5 fw-bold text-dark mb-2">
        {t("superAdmin.brands.title", "Brand Management")}
      </h4>
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.brands.hint",
          "Before Adding a brand check if the brand or brand with similar name exists",
        )}
      </small>

      <div className="mb-3 d-flex align-items-center gap-3">
        <input
          type="text"
          className="form-control"
          placeholder={t(
            "superAdmin.brands.searchPlaceholder",
            "Search brands...",
          )}
          value={brandSearchTerm}
          onChange={(e) => setBrandSearchTerm(e.target.value)}
          style={{ maxWidth: 500 }}
        />
        <div className="d-flex flex-column align-items-start">
          <button
            className="btn btn-light"
            style={{
              color: "var(--brand-color-dark)",
              borderColor: "var(--brand-color-dark)",
            }}
            onClick={() => setShowAddBrandModal(true)}
          >
            <i className="bi bi-plus-circle"> </i>{" "}
            {t("superAdmin.brands.addBrand", "Brand")}
          </button>
        </div>
      </div>

      <FiveColumnTable
        data={filteredBrands}
        isLoaded={allBrandsLoaded}
        error={allBrandErrors}
        column1Key={"client_id"}
        column1Title={t("superAdmin.brands.clientId", "Client ID")}
        column2Key={"name_display"}
        column2Title={t("superAdmin.brands.brandName", "Brand Name")}
        column3Key={"domains"}
        column3Title={t("superAdmin.brands.domains", "Domains")}
        column4Key={"brand_name_variations"}
        column4Title={t(
          "superAdmin.brands.brandNameVariations",
          "Brand Name Variations",
        )}
        column5Key={"actions"}
        column5Title={t("common.actions", "Actions")}
        columnRatio="20-15-30-30-5"
      />

      {/* Add Brand Modal */}
      {showAddBrandModal && (
        <BrandModal
          isEdit={false}
          brandFormData={brandFormData}
          handleBrandInputChange={handleBrandInputChange}
          addPendingValue={addPendingValue}
          isBrandSubmitting={isBrandSubmitting}
          setShowAddBrandModal={setShowAddBrandModal}
          setShowEditBrandModal={setShowEditBrandModal}
          resetBrandForm={resetBrandForm}
          handleBrandSubmit={handleBrandSubmit}
          handleBrandEditSubmit={handleBrandEditSubmit}
          allClients={allClients}
          allClientsLoaded={allClientsLoaded}
        />
      )}

      {/* Edit Brand Modal */}
      {showEditBrandModal && (
        <BrandModal
          isEdit={true}
          brandFormData={brandFormData}
          handleBrandInputChange={handleBrandInputChange}
          addPendingValue={addPendingValue}
          isBrandSubmitting={isBrandSubmitting}
          setShowAddBrandModal={setShowAddBrandModal}
          setShowEditBrandModal={setShowEditBrandModal}
          resetBrandForm={resetBrandForm}
          handleBrandSubmit={handleBrandSubmit}
          handleBrandEditSubmit={handleBrandEditSubmit}
          allClients={allClients}
          allClientsLoaded={allClientsLoaded}
        />
      )}

      {/* Delete Brand Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(brandToDelete)}
        title={t("superAdmin.brands.confirmDeleteTitle", "Confirm Delete")}
        message={t("superAdmin.brands.deleteConfirm", `Are you sure you want to delete ${brandToDelete?.name}?`, { name: brandToDelete?.name })}
        warningMessage={
          brandToDelete && needsReview(brandToDelete)
            ? t(
                "superAdmin.brands.deleteRejectsValues",
                "Its auto-created and suggested names and domains will be rejected and won't be auto-created again.",
              )
            : null
        }
        confirmText={t("common.delete", "Delete")}
        cancelText={t("common.cancel", "Cancel")}
        submittingText={t("common.deleting", "Deleting...")}
        confirmVariant="danger"
        isSubmitting={isBrandDeleting}
        onCancel={() => setBrandToDelete(null)}
        onConfirm={handleConfirmDeleteBrand}
        confirmTestId="confirm-delete-brand-btn"
      />
    </div>
  );
}

export default SuperAdminBrand;
